import React from 'react';
import { FileText, GitBranch, BarChart2, Tags } from 'lucide-react';
import { Card } from './common';
import { useStyles } from '../contexts/StyleContext';

const ToolCard = ({ icon: Icon, title, description }) => {
  const styles = useStyles();
  const { card, header } = styles;

  return (
    <Card className={`${card.container.base} hover:shadow-lg transition-shadow duration-200`}>
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div 
            className="p-3 rounded-lg text-white"
            style={{ background: header.title.color }}
          >
            <Icon size={24} />
          </div>
        </div>
        <div className="flex-grow">
          <h4 className={header.subtitle.base} style={{ color: header.subtitle.color }}>
            {title}
          </h4>
          <p className={card.content.base} style={{ color: card.content.color }}>
            {description}
          </p>
        </div>
      </div>
    </Card>
  );
};

const Overview = () => {
  const styles = useStyles();
  const { card, header, layout } = styles;

  const tools = [
    {
      icon: FileText,
      title: "5 Whys Analysis",
      description: "A systematic technique for exploring cause-and-effect relationships underlying a particular problem. Helps identify the root cause by repeatedly asking 'why' the problem occurs."
    },
    {
      icon: GitBranch,
      title: "Fishbone Diagram",
      description: "Also known as the Ishikawa diagram, this visual tool helps identify and categorize potential causes of a problem, organizing them into distinct categories for comprehensive analysis."
    },
    {
      icon: BarChart2,
      title: "Chart Generation",
      description: "Create visual representations of data through Pareto charts and histograms. These charts help identify the most significant factors and patterns in your analysis."
    },
    {
      icon: Tags,
      title: "Root Cause Classification",
      description: "Systematically categorize and organize identified root causes to understand patterns and common issues, facilitating more effective problem-solving strategies."
    }
  ];

  return (
    <div className={layout.section.base}>
      <Card className={card.container.base}>
        <h3 className={header.subtitle.base} style={{ color: header.subtitle.color }}>
          Overview
        </h3>
        <p className={card.content.base} style={{ color: card.content.color }}>
          Root Cause Identification tools help in systematically analyzing problems to identify their underlying causes. 
          Use these comprehensive tools to conduct a thorough analysis and develop effective solutions.
        </p>
      </Card>

      <div className={layout.grid.base}>
        {tools.map((tool, index) => (
          <ToolCard key={index} {...tool} />
        ))}
      </div>

      <Card className={card.container.base}>
        <h3 className={header.subtitle.base} style={{ color: header.subtitle.color }}>
          Getting Started
        </h3>
        <div className="space-y-4">
          <p className={card.content.base} style={{ color: card.content.color }}>
            Begin your analysis by selecting the most appropriate tool for your needs:
          </p>
          <ul className="space-y-3">
            {tools.map((tool, index) => (
              <li key={index} className="flex items-start space-x-3">
                <div 
                  className="flex-shrink-0 w-1.5 h-1.5 mt-2 rounded-full"
                  style={{ backgroundColor: header.title.color }}
                />
                <span className={card.content.base} style={{ color: card.content.color }}>
                  Use <span className="font-medium" style={{ color: header.subtitle.color }}>
                    {tool.title}
                  </span> {tool.description}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default Overview;
